import { useCurrencyFormatter } from '@mint-lib/formatting-hooks';
import { useTranslation } from '@mint-lib/i18n';
import { Calendar, ChartPie, Money } from '@mint-lib/icons';
import { useFlags } from '@mint-lib/routing-context';
import { Chip, Divider, typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { useFlightSummaryApi } from '../../../../../api/queries/useFlightSummaryApi.js';
import { useChosenLevel } from '../../../../../hooks/useChosenLevel.js';
import { useChosenVersionId } from '../../../../../hooks/useChosenVersionId.js';
import { VersioningDrawer } from '../../DrawersPanel/Drawers/VersioningDrawer/VersioningDrawer.jsx';
import ApprovalCircuit from './ApprovalCircuit.jsx';
import { ArchivedVersionNotice } from './ArchivedVersionNotice.jsx';
import { BaseSummarySkeleton } from './BaseSkeleton.jsx';
import LevelDateRange from './LevelDateRange.jsx';

export const FlightSummary = () => {
  const { t } = useTranslation('@myn/planning_v2');
  const ff = useFlags('@mint/arm', ['newApprovalMicro']);
  const version = useChosenVersionId();
  const { id } = useChosenLevel();

  const { data: flightSummary, isPending } = useFlightSummaryApi();

  const currencyFormatter = useCurrencyFormatter();

  const formatBudget = (budget: string) => {
    return currencyFormatter.format(Number(budget), {
      currency: flightSummary?.currency ?? 'USD',
    })?.formatted;
  };

  if (isPending || !flightSummary?.dateFrom || !flightSummary?.dateTo) {
    return <BaseSummarySkeleton />;
  }

  const bodyShort01 = typographyVariants({ variant: 'bodyShort01' });
  const bodyShort02 = typographyVariants({ variant: 'bodyShort02' });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex min-h-8 flex-wrap gap-x-4 gap-y-2">
        <div className="flex grow items-center gap-1">
          <Chip variant="outline" color="silver">
            <Calendar />
            <span>{t('Flight')}</span>
          </Chip>
          <Chip variant="outline" color="silver">
            <Calendar />
            <LevelDateRange
              dateFrom={flightSummary?.dateFrom}
              dateTo={flightSummary?.dateTo}
            />
          </Chip>
          <Divider orientation="vertical" className="mx-1 self-stretch" />
          <VersioningDrawer />
          {version && <ArchivedVersionNotice />}
        </div>
        {ff.newApprovalMicro.enabled && !version && id && (
          <ApprovalCircuit
            flightMediaDataId={id}
            flightName={flightSummary.name}
            flightCurrency={flightSummary.currency}
          />
        )}
      </div>
      <h1 className={typographyVariants({ variant: 'h3' })}>
        {flightSummary?.name}
      </h1>
      <div className="flex items-center gap-2">
        <Money />
        <span className={cn(bodyShort02, 'mr-1')}>{t('Planned budget')}</span>
        <Divider orientation="vertical" className="mx-1 self-stretch" />
        <div className="flex">
          <span className={cn(bodyShort01, 'mr-1')}>{t('Media plan:')}</span>
          <span className={bodyShort02}>
            {formatBudget(flightSummary.plannedMediaPlanBudget)}
          </span>
        </div>
        <div className="ml-4 flex">
          <span className={cn(bodyShort01, 'mr-1')}>
            {t('Total Activity:')}
          </span>
          <span className={bodyShort02}>
            {formatBudget(flightSummary.plannedActivityBudget)}
          </span>
        </div>
        <div className="ml-4 flex">
          <span className={cn(bodyShort01, 'mr-1')}>{t('Flight:')}</span>
          <span className={bodyShort02}>
            {formatBudget(flightSummary.plannedBudget)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <ChartPie />
        <span className={cn(bodyShort02, 'mr-1')}>
          {t('Flight Net media budget')}
        </span>
        <Divider orientation="vertical" className="mx-1 self-stretch" />
        <div className="flex">
          <span className={cn(bodyShort01, 'mr-1')}>{t('Allocated:')}</span>
          <span className={bodyShort02}>
            {formatBudget(flightSummary.allocatedBudget)}
          </span>
          <Divider orientation="vertical" className="mx-1.5 self-stretch" />
          <span className={bodyShort02}>
            {t('{{percentage}}% of planned', {
              percentage: flightSummary.allocatedBudgetPercentage,
            })}
          </span>
        </div>
        <div className="ml-4 flex">
          <span className={cn(bodyShort01, 'mr-1')}>{t('Unallocated:')}</span>
          <span className={bodyShort02}>
            {formatBudget(flightSummary.unallocatedBudget)}
          </span>
          <Divider orientation="vertical" className="mx-1.5 self-stretch" />
          <span className={bodyShort02}>
            {t('{{percentage}}% of planned', {
              percentage: flightSummary.unallocatedBudgetPercentage,
            })}
          </span>
        </div>
      </div>
    </div>
  );
};
